/**
 * EmailScanResults — shows detected providers after email scan.
 * Adapted from frontend/src/components/EmailScanResults.tsx for React Native.
 */
import { useState, useEffect } from 'react'
import { View, Text, Pressable, ActivityIndicator, StyleSheet } from 'react-native'
import { Zap, Smartphone, Wifi, Search, CheckCircle, AlertCircle } from 'lucide-react-native'
import { apiClient } from '../api/client'
import { colors, spacing, borderRadius, typography } from '../theme'

interface DetectedProvider {
  name: string
  domain: string
  confidence: string
  email_count: number
  categories: string[]
}

interface EmailScanResultsProps {
  connectionId: string
  onComplete?: (providers: DetectedProvider[]) => void
  autoStart?: boolean
}

const CATEGORY_META: Record<string, { Icon: typeof Zap; bg: string; color: string; label: string }> = {
  energy: { Icon: Zap, bg: '#fef9c3', color: '#ca8a04', label: 'Strøm' },
  mobile: { Icon: Smartphone, bg: '#dbeafe', color: '#2563eb', label: 'Mobil' },
  internet: { Icon: Wifi, bg: '#f3e8ff', color: '#9333ea', label: 'Internet' },
}

export default function EmailScanResults({ connectionId, onComplete, autoStart = true }: EmailScanResultsProps) {
  const [scanning, setScanning] = useState(false)
  const [providers, setProviders] = useState<DetectedProvider[]>([])
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const runDetect = async () => {
    setScanning(true)
    setError('')
    try {
      const res = await apiClient.email.detectProviders(connectionId)
      const raw = res.data?.providers || []
      // Backend returns provider_name, frontend interface uses name
      const detected = raw.map((p: any) => ({
        ...p,
        name: p.name || p.provider_name,
      }))
      setProviders(detected)
      setDone(true)
      onComplete?.(detected)
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Scanning fejlede'
      setError(msg)
    } finally {
      setScanning(false)
    }
  }

  useEffect(() => {
    if (autoStart && connectionId) {
      runDetect()
    }
  }, [connectionId, autoStart])

  if (scanning) {
    return (
      <View style={styles.center}>
        <View style={styles.scanIconWrap}>
          <Search size={28} color={colors.primary} />
          <ActivityIndicator size="small" color={colors.primary} style={styles.spinnerOverlay} />
        </View>
        <Text style={styles.scanTitle}>Scanner din indbakke...</Text>
        <Text style={styles.scanSub}>Vi leder efter emails fra el-, mobil- og bredbåndsselskaber</Text>
      </View>
    )
  }

  if (error) {
    return (
      <View style={styles.errorCard}>
        <AlertCircle size={20} color={colors.error} />
        <View style={{ flex: 1 }}>
          <Text style={styles.errorTitle}>Scanning fejlede</Text>
          <Text style={styles.errorMsg}>{error}</Text>
        </View>
        <Pressable onPress={runDetect}>
          <Text style={styles.retryText}>Prøv igen</Text>
        </Pressable>
      </View>
    )
  }

  if (done && providers.length === 0) {
    return (
      <View style={styles.center}>
        <Search size={28} color={colors.text.muted} />
        <Text style={styles.emptyTitle}>Vi fandt ingen selskaber i din email-historik.</Text>
        <Text style={styles.emptySub}>Ingen bekymring — du kan stadig udfylde dine aftaler manuelt.</Text>
      </View>
    )
  }

  if (!done) return null

  return (
    <View style={styles.results}>
      <View style={styles.resultHeader}>
        <CheckCircle size={18} color={colors.success} />
        <Text style={styles.resultTitle}>
          Vi fandt {providers.length} selskab{providers.length !== 1 ? 'er' : ''}
        </Text>
      </View>
      {providers.map((provider, idx) => {
        const cats = provider.categories || []
        return (
          <View key={idx} style={styles.providerCard}>
            <View style={styles.catIcons}>
              {cats.map((cat) => {
                const meta = CATEGORY_META[cat]
                if (!meta) return null
                const { Icon } = meta
                return (
                  <View key={cat} style={[styles.catIcon, { backgroundColor: meta.bg }]}>
                    <Icon size={14} color={meta.color} />
                  </View>
                )
              })}
              {cats.length === 0 && (
                <View style={[styles.catIcon, { backgroundColor: colors.surface }]}>
                  <Search size={14} color={colors.text.muted} />
                </View>
              )}
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.providerName} numberOfLines={1}>{provider.name}</Text>
              <Text style={styles.providerMeta}>
                {provider.email_count} email{provider.email_count !== 1 ? 's' : ''} fundet
                {provider.confidence === 'high' ? ' — sikker match' : ''}
              </Text>
            </View>
            <CheckCircle size={16} color={colors.success} />
          </View>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  center: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
    gap: spacing.sm,
  },
  scanIconWrap: {
    position: 'relative',
    marginBottom: spacing.xs,
  },
  spinnerOverlay: {
    position: 'absolute',
    bottom: -4,
    right: -4,
  },
  scanTitle: {
    ...typography.captionBold,
    color: colors.text.primary,
  },
  scanSub: {
    ...typography.small,
    color: colors.text.muted,
    textAlign: 'center',
  },
  errorCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    backgroundColor: '#fef2f2',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorTitle: {
    ...typography.captionBold,
    color: '#991b1b',
  },
  errorMsg: {
    ...typography.small,
    color: colors.error,
  },
  retryText: {
    ...typography.small,
    color: '#991b1b',
    fontWeight: '600',
  },
  emptyTitle: {
    ...typography.caption,
    color: colors.text.secondary,
  },
  emptySub: {
    ...typography.small,
    color: colors.text.muted,
    textAlign: 'center',
  },
  results: {
    gap: spacing.sm,
  },
  resultHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  resultTitle: {
    ...typography.captionBold,
    color: colors.text.primary,
  },
  providerCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    backgroundColor: '#fff',
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  catIcons: {
    flexDirection: 'row',
    gap: 4,
  },
  catIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  providerName: {
    ...typography.captionBold,
    color: colors.text.primary,
  },
  providerMeta: {
    ...typography.small,
    color: colors.text.muted,
  },
})
